import { useState } from "react";
import { Sparkles, Check, Bell, Share2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SOAP_IMAGE = "/images/soap-coming-soon.jpg";

export const SoapComingSoon = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | 'registered' | 'already_registered'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.phone.trim() && !form.email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          product: "body-soap",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data.message === "already_registered" ? "already_registered" : "registered");
      }
    } catch (err) {
      /* noop */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-10 sm:py-14 px-4 sm:px-6" data-testid="soap-coming-soon" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 rounded-3xl overflow-hidden bg-white border border-[#E8E4DC] shadow-sm">
          {/* Image */}
          <div className="relative h-56 md:h-auto md:min-h-[420px] overflow-hidden">
            <img src={SOAP_IMAGE} alt="סבון גוף טבעי בעבודת יד M'art" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#4A5D43] text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                <Sparkles className="h-3.5 w-3.5" />
                בקרוב
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 flex flex-col justify-center text-right">
            <p className="text-[#C47F64] text-xs font-medium tracking-wider uppercase mb-2">🌿 משהו חדש מתבשל אצלנו</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2C3329] font-medium mb-3 leading-snug">
              סבון גוף טבעי
            </h2>
            <p className="text-sm sm:text-base text-[#4A4A42] leading-relaxed mb-3">
              ממש עכשיו אנחנו מריצים סדרה ראשונה ומצומצמת של סבוני גוף מוצקים ועדינים בעבודת יד.
            </p>
            <p className="text-sm text-[#6B7266] leading-relaxed mb-4">
              הסבון החדש שלנו מבוסס על רכיבים מהטבע, על בסיס גליצרין ותמצית שמן זית / דבש (בלי חומרים מגרים או סולפטים),
              יחד עם נגיעה קלה של סודה לשתייה עדינה למניעת ריחות לא נעימים, שמעניקה תחושת פילינג רכה, ניקוי עמוק והמון רעננות.
            </p>

            <div className="space-y-2 mb-5">
              <div className="flex items-start gap-2.5">
                <Share2 className="h-4 w-4 text-[#4A5D43] mt-0.5 shrink-0" />
                <p className="text-sm text-[#4A4A42]"><strong>שתפו בקהילה:</strong> התמיכה שלכם בשיתוף הקהילה וברכישת הדאודורנט שלנו באתר עוזרת לנו להמשיך לפתח מוצרים מעולים.</p>
              </div>
              <div className="flex items-start gap-2.5">
                <Bell className="h-4 w-4 text-[#4A5D43] mt-0.5 shrink-0" />
                <p className="text-sm text-[#4A4A42]"><strong>הצטרפו לרשימת עדכונים:</strong> הצטרפו בלחיצת כפתור לרשימת ההמתנה, ונהיה בקשר ברגע שהסבונים מוכנים.</p>
              </div>
            </div>

            {status ? (
              <div className="bg-[#4A5D43]/8 border border-[#4A5D43]/20 rounded-2xl p-5 text-center" data-testid="soap-waitlist-success">
                <div className="w-12 h-12 rounded-full bg-[#4A5D43] flex items-center justify-center mx-auto mb-3">
                  <Check className="h-6 w-6 text-white" />
                </div>
                <p className="font-serif text-lg text-[#2C3329] mb-1">
                  {status === "already_registered" ? "אתם כבר ברשימה!" : "תודה, נרשמתם בהצלחה!"}
                </p>
                <p className="text-sm text-[#6B7266]">נעדכן אתכם ברגע שהסבונים מוכנים. 🌿</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="soap-waitlist-form">
                <input
                  type="text"
                  required
                  placeholder="שם מלא"
                  data-testid="soap-waitlist-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-full border border-[#E8E4DC] text-sm text-[#2C3329] placeholder:text-[#9A9790] focus:outline-none focus:border-[#4A5D43] transition-colors"
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    type="tel"
                    placeholder="טלפון"
                    data-testid="soap-waitlist-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-full border border-[#E8E4DC] text-sm text-[#2C3329] placeholder:text-[#9A9790] focus:outline-none focus:border-[#4A5D43] transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="אימייל"
                    data-testid="soap-waitlist-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-full border border-[#E8E4DC] text-sm text-[#2C3329] placeholder:text-[#9A9790] focus:outline-none focus:border-[#4A5D43] transition-colors"
                  />
                </div>
                <p className="text-[11px] text-[#9A9790] px-1">יש להשאיר טלפון או אימייל אחד לפחות</p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="soap-waitlist-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#C47F64] hover:bg-[#b06f55] disabled:opacity-60 text-white rounded-full px-6 py-3.5 text-sm font-medium transition-colors"
                >
                  <Bell className="h-4 w-4" />
                  {submitting ? "רושם..." : "הצטרפו לרשימת עדכונים"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SoapComingSoon;
